/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { BarChart, BarChartWithLine, ExpandButton, Select } from '@components';
import { trimSpace, formatFirstCharacter } from '@utils/widget';
import D_Number from '@utils/number';
import {
  getValueFromSearchParam,
  removeValueFromSearchParam,
  setURLSearchParam,
} from '@utils/history';
import searchParamKeys from '@model/searchParamKeys';
import { useHistory } from 'react-router-dom';
export const notPercentValue = ['eps', 'market Cap'];

export const SwitchConfigInChart = ({
  label,
  options,
  defaultValue,
  switchCondition,
  value,
}) => (
  <div className="conditionSelection">
    <label>{label}</label>
    <Select
      size="sm"
      defaultValue={defaultValue}
      style={{ width: 130 }}
      options={options}
      value={value}
      onSelect={(e) => {
        switchCondition(label, e);
      }}
    />
  </div>
);

export const reflowChart = (...charts) => {
  try {
    charts.forEach((chart) => {
      chart && chart.current && chart.current.reflow && chart.current.reflow();
    });
  } catch (error) {
    console.error(error);
  }
};

export const destroyCharts = (...charts) => {
  try {
    charts.forEach((chart) => {
      chart &&
        chart.current &&
        chart.current.destroy &&
        chart.current.destroy();
    });
  } catch (error) {
    console.error(error);
  }
};

const GrossProfit = ({
  title,
  condition,
  others: data = {},
  buyerManualInputFlag,
  targetManualInputFlag,
}) => {
  const { expand: initialExpand } = getValueFromSearchParam(
    searchParamKeys.expand,
  );
  const history = useHistory();
  const [choosedTimeline, setChoosedTimeline] = useState('ltmForQuarter');
  const [choosedMetrics, setChoosedMetrics] = useState('');
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [expand, setExpand] = useState(initialExpand);
  const barRefs = [useRef(null), useRef(null)];
  const barLineRefs = [useRef(null), useRef(null)];
  const [chartCondition, setChartCondition] = useState(condition);
  const [barYMax, setBarYMax] = useState(0);
  const [barYMin, setBarYMin] = useState(0);
  const [barLineYMax, setBarLineYMax] = useState({
    leftMax: 0,
    rightMax: 0,
    leftMin: 0,
    rightMin: 0,
  });
  const handleMetricsWhenChooseTimeline = (timeline) => {
    if (timeline === 'ltmForQuarter' || timeline === '1yForQuarter') {
      setChartCondition({
        Timeline: condition.Timeline,
        Metrics: condition.Metrics.filter((m) => m.value !== 'eps'),
      });
    } else {
      setChartCondition(condition);
    }
  };
  const swichConditionHandler = (type, value) => {
    if (type === 'Timeline') {
      if (value === choosedTimeline) {
        return;
      }
      setChoosedMetrics('');
      setChoosedTimeline(value);
    } else {
      if (value === choosedMetrics) {
        return;
      }
      setChoosedMetrics(value);
    }
    setCategories([]);
    setSeries([]);
  };

  const buildCategories = () => {
    const targets = Object.keys(data);
    const categories = targets.map((target) => {
      const grossProfits = data[target][choosedTimeline].grossProfitList;
      const values =
        grossProfits &&
        grossProfits.map((grossProfit) => grossProfit.displayEndDate);
      return { [target]: values };
    });
    setCategories(categories);
  };

  const buildSeries = () => {
    const targets = Object.keys(data);
    const series = targets.map((target) => {
      const seryList = [];
      if (choosedMetrics === '') {
        const grossProfits = data[target][choosedTimeline].grossProfitList;
        const unitType = data[target][choosedTimeline].yAxis.grossProfit;
        const values = grossProfits.map((grossProfit) => grossProfit.value);

        grossProfits[0] &&
          seryList.push({
            name: 'Gross Profit',
            data: values,
            unitType,
          });
      } else {
        // timeline
        const grossProfits = data[target][choosedTimeline].grossProfitList;
        const unitType = data[target][choosedTimeline].yAxis.grossProfit;
        const values = grossProfits.map((grossProfit) => grossProfit.value);
        grossProfits[0] &&
          seryList.push({
            name: 'Gross Profit',
            data: values,
            unitType,
          });
        // metics
        const metricsKey = trimSpace(choosedMetrics);
        const metrics = data[target][choosedTimeline].metrics[metricsKey];
        const metricsValues = grossProfits.map((grossProfit) => null);
        metrics.map((metric, idx) => {
          const metricIdx = grossProfits.findIndex(
            (grossProfit) =>
              grossProfit.displayEndDate === metric.displayEndDate,
          );
          metricsValues[metricIdx] = metric.value;
          return metric.value;
        });

        metricsValues.find((m) => m != null && m !== '') &&
          seryList.push({
            name:
              choosedMetrics === 'eps'
                ? 'EPS'
                : formatFirstCharacter(choosedMetrics),
            data: notPercentValue.includes(choosedMetrics)
              ? metricsValues
              : metricsValues.map((v) => (v === 0 ? null : v)),
            unitType: data[target][choosedTimeline].yAxis[metricsKey],
          });
      }
      // console.log(seryList);
      return { [target]: seryList };
    });

    setSeries(series);
  };

  useEffect(() => {
    buildCategories();
    buildSeries();
    handleMetricsWhenChooseTimeline(choosedTimeline);
  }, [choosedTimeline, choosedMetrics, data]);

  useEffect(() => {
    reflowChart(...barRefs, ...barLineRefs);
  }, [expand]);

  useEffect(
    () => () => {
      destroyCharts(...barRefs, ...barLineRefs);
    },
    [],
  );
  useEffect(() => {
    // ✅ Fixed version with proper null checks
    if (barRefs[0].current || barRefs[1].current) {
      const barMax = barRefs
        .filter((c) => c.current && c.current.yAxis && c.current.yAxis[0]) // Filter out null/invalid refs
        .map((c) => c.current.yAxis[0].max);

      const barMin = barRefs
        .filter((c) => c.current && c.current.yAxis && c.current.yAxis[0]) // Filter out null/invalid refs
        .map((c) => c.current.yAxis[0].min);

      if (barMax.length > 0 && barMin.length > 0) {
        const barYAxisMax = D_Number.getArrayMaxNumber(barMax);
        const barYAxisMin = D_Number.getArrayMinNumber(barMin);
        setBarYMax(barYAxisMax);
        setBarYMin(barYAxisMin);
      }
    }
  }, [barRefs]);

  useEffect(() => {
    // ✅ Fixed version with proper null checks
    const validBarLineRefs = barLineRefs.filter(
      (c) =>
        c.current &&
        c.current.yAxis &&
        c.current.yAxis[0] &&
        c.current.yAxis[1],
    );

    if (validBarLineRefs.length === 2) {
      // Ensure we have both charts
      const barLineMax = {
        leftMax: validBarLineRefs.map((c) => c.current.yAxis[0].max),
        rightMax: validBarLineRefs.map((c) => c.current.yAxis[1].max),
        leftMin: validBarLineRefs.map((c) => c.current.yAxis[0].min),
        rightMin: validBarLineRefs.map((c) => c.current.yAxis[1].min),
      };

      const barLineYAxisMax = {
        leftMax: D_Number.getArrayMaxNumber(barLineMax.leftMax),
        rightMax: D_Number.getArrayMaxNumber(barLineMax.rightMax),
        leftMin: D_Number.getArrayMinNumber(barLineMax.leftMin),
        rightMin: D_Number.getArrayMinNumber(barLineMax.rightMin),
      };

      if (
        barLineYAxisMax.leftMax !== barLineYMax.leftMax ||
        barLineYAxisMax.rightMax !== barLineYMax.rightMax ||
        barLineYAxisMax.leftMin !== barLineYMax.leftMin ||
        barLineYAxisMax.rightMin !== barLineYMax.rightMin
      ) {
        setBarLineYMax(barLineYAxisMax);
      }
    }
  }, [barLineRefs]);

  const onExpandClick = () => {
    setExpand(!expand);
    if (!expand) {
      setURLSearchParam(history, {
        [searchParamKeys.expand]: true,
      });
    } else {
      removeValueFromSearchParam(history, [searchParamKeys.expand]);
    }
  };

  console.log('BarChartWithLine gp logs series :', series);

  return (
    <div className={`analysis-right-wrapper ${expand && 'full-screen'}`}>
      <div className="title expand-box">
        <div>{title}</div>
        {data && (
          <ExpandButton
            initialExpand={initialExpand}
            onClick={onExpandClick}
            scrollPageFooter
          />
        )}
      </div>

      <div className="common-chart-wrapper">
        {(buyerManualInputFlag && targetManualInputFlag) ||
        (data?.buyer?.noDataFlag && data?.target?.noDataFlag) ? null : (
          <div className="conditionContainer">
            <div className="conditionList">
              {Object.keys(chartCondition).map((k, idx) => (
                <SwitchConfigInChart
                  key={`${k}-${idx}`}
                  label={k}
                  options={chartCondition[k]}
                  defaultValue={
                    k === 'Timeline' ? choosedTimeline : choosedMetrics
                  }
                  switchCondition={swichConditionHandler}
                  value={k === 'Timeline' ? choosedTimeline : choosedMetrics}
                />
              ))}
            </div>
          </div>
        )}

        <div className="chartList">
          {Object.keys(data).map((target, idx, items) => {
            const propsSeries = series.length ? series[idx][target] : [];
            const isLast = items.length - 1 === idx;
            const propsCategories = categories.length
              ? categories[idx][target]
              : [];
            if (choosedMetrics !== '') {
              return (
                <>
                  <BarChartWithLine
                    ref={barLineRefs[idx]}
                    key={`${target}-${idx}`}
                    containerId={`bar-chart-${idx}`}
                    companyName={data[target].companyName}
                    noDataFlag={data[target].noDataFlag}
                    categories={propsCategories}
                    series={propsSeries}
                    metrics={choosedMetrics}
                    secondaryXAxis={
                      data[target][choosedTimeline]?.xAxis?.secondLine || null
                    }
                    yMax={barLineYMax}
                  />
                  {!isLast && <div className="line" />}
                </>
              );
            }
            return (
              <>
                <BarChart
                  ref={barRefs[idx]}
                  key={`${target}-${idx}`}
                  containerId={`bar-chart-${idx}`}
                  companyName={data[target].companyName}
                  noDataFlag={data[target].noDataFlag}
                  categories={propsCategories}
                  series={propsSeries}
                  secondaryXAxis={
                    data[target][choosedTimeline]?.xAxis?.secondLine || null
                  }
                  yMax={barYMax}
                  yMin={barYMin}
                />
                {!isLast && <div className="line" />}
              </>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(GrossProfit);
